import React from 'react';
import {
  isChunkLoadError,
  reloadForLatestChunk,
} from '../utils/chunkLoadRecovery';
import { reportReactError } from '../utils/errorReporter';

function RootFallback({
  error,
  onReset,
  isRecoverableChunkError,
}: {
  error: Error;
  onReset: () => void;
  isRecoverableChunkError: boolean;
}) {
  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'var(--bg-page)',
        fontFamily: 'var(--font-sans)',
        gap: 'var(--spacing-3)',
        padding: 'var(--spacing-6)',
        textAlign: 'center',
      }}
    >
      <p style={{ fontSize: '14px', color: 'var(--text-primary)' }}>
        {isRecoverableChunkError ? 'App update needed' : 'Something went wrong'}
      </p>
      {isRecoverableChunkError && (
        <p
          style={{
            fontSize: '13px',
            color: 'var(--text-muted)',
            maxWidth: '420px',
            margin: 0,
          }}
        >
          The app has been updated. Refresh to load the latest files.
        </p>
      )}
      <p
        style={{
          fontSize: '12px',
          color: 'var(--text-muted)',
          maxWidth: '400px',
          fontFamily: 'var(--font-mono)',
          background: 'var(--bg-subtle)',
          padding: 'var(--spacing-3)',
          borderRadius: 'var(--radius-md)',
          border: '1px solid var(--border-default)',
        }}
      >
        {error.message}
      </p>
      <button
        onClick={onReset}
        style={{
          padding: 'var(--spacing-2) var(--spacing-5)',
          fontSize: '13px',
          borderRadius: 'var(--radius-md)',
          border: '1px solid var(--border-default)',
          background: 'var(--bg-surface)',
          color: 'var(--text-primary)',
          cursor: 'pointer',
          fontFamily: 'var(--font-sans)',
        }}
      >
        {isRecoverableChunkError ? 'Refresh' : 'Try again'}
      </button>
    </div>
  );
}

type RootErrorBoundaryProps = {
  children: React.ReactNode;
};

type RootErrorBoundaryState = {
  error: Error | null;
};

export class RootErrorBoundary extends React.Component<
  RootErrorBoundaryProps,
  RootErrorBoundaryState
> {
  state: RootErrorBoundaryState = {
    error: null,
  };

  static getDerivedStateFromError(error: Error): RootErrorBoundaryState {
    return { error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    console.error('[RootErrorBoundary]', error, info);

    if (!isChunkLoadError(error)) {
      reportReactError(error, info.componentStack);
    }

    if (isChunkLoadError(error)) {
      reloadForLatestChunk();
    }
  }

  handleReset = () => {
    if (this.state.error && isChunkLoadError(this.state.error)) {
      window.location.reload();
      return;
    }

    this.setState({ error: null });
  };

  render() {
    if (this.state.error) {
      const isRecoverableChunkError = isChunkLoadError(this.state.error);
      return (
        <RootFallback
          error={this.state.error}
          isRecoverableChunkError={isRecoverableChunkError}
          onReset={this.handleReset}
        />
      );
    }

    return this.props.children;
  }
}
